'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';
import { styled } from '@mui/material/styles';
import { shouldForwardProp } from 'src/components/Particles/Particles';
interface LinkProps extends NextLinkProps {
  children: React.ReactNode;
  noDecoration?: boolean;
  fullWidth?: boolean;
}
const StyledLink = styled(NextLink, {
  ...shouldForwardProp(['noDecoration', 'fullWidth'])
})<{
  noDecoration?: boolean;
  fullWidth?: boolean;
}>(({
  theme,
  noDecoration,
  fullWidth
}) => ({
  width: fullWidth ? '100%' : 'auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.sizes.spacing['02'],
  flexSelf: 'stretch',
  color: theme.colors.text.link,
  textDecorationColor: theme.colors.text.link,
  fontSize: theme.fonts.size.base,
  fontFamily: theme.fonts.family.body,
  fontStyle: 'normal',
  fontWeight: theme.fonts.weight.normal,
  lineHeight: theme.fonts.lineHeight['leading-6'],
  letterSpacing: theme.fonts.letterSpacing.normal,
  '&:hover': {
    color: theme.colors.text.link,
    textDecorationColor: theme.colors.text.link
  },
  '&:focus': {
    color: theme.colors.text.link,
    textDecorationColor: theme.colors.text.link
  },
  '&:active': {
    color: theme.colors.text.link,
    textDecorationColor: theme.colors.text.link
  },
  ...(noDecoration ? {
    textDecoration: 'none'
  } : {
    textDecoration: 'underline',
    textDecorationStyle: 'solid',
    textDecorationSkipInk: 'none',
    textDecorationThickness: 'auto',
    textUnderlineOffset: 'auto',
    textUnderlinePosition: 'from-font'
  })
}));

/**
 * @name Link
 * @description A styled link component that uses Next.js Link for client-side navigation.
 * It supports custom styling via styled extension, styles and class names.
 * @props {string} href - Default prop - The URL to link to.
 * @props {React.ReactNode} children - Default prop - The content to display inside the link.
 * @props {boolean} noDecoration - Custom prop - If true, removes the default underline decoration from the link.
 */

const MyLink: React.FC<LinkProps> = t0 => {
  const $ = _c(12);
  let children;
  let href;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    const {
      href: t3,
      children: t4,
      noDecoration: t5,
      fullWidth: t6,
      as: _,
      ...t7
    } = t0;
    href = t3;
    children = t4;
    t1 = t5;
    t2 = t6;
    props = t7;
    $[0] = t0;
    $[1] = children;
    $[2] = href;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    href = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const noDecoration = t1 === undefined ? false : t1;
  const fullWidth = t2 === undefined ? true : t2;
  let t3;
  if ($[6] !== children || $[7] !== fullWidth || $[8] !== href || $[9] !== noDecoration || $[10] !== props) {
    t3 = <StyledLink href={href} noDecoration={noDecoration} fullWidth={fullWidth} {...props}>{children}</StyledLink>;
    $[6] = children;
    $[7] = fullWidth;
    $[8] = href;
    $[9] = noDecoration;
    $[10] = props;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
export default MyLink;
export type { LinkProps };